import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
} from '@mui/material';
import {
  Search as SearchIcon,
  CameraAlt as CameraIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Description as DescriptionIcon,
  AssignmentTurnedIn as ApprovementIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from './Layout';
import { useAuth } from '../contexts/AuthContext';
import { nominationsAPI, containersAPI, documentsAPI, usersAPI } from '../services/api';

const AVG_GROSS_OPTIONS = [25, 50, 1000] as const;
const TARE_WEIGHT_PRESETS = [80, 150] as const;

const TARE_GR_TO_KG = 1000;

/** Total Net (kg) = Total Gross (kg) − (Total Tare (gr) / 1000) */
function getDisplayTotalNetKg(grossKg: number | null | undefined, tareGr: number | null | undefined): number | null {
  if (grossKg == null || tareGr == null || Number.isNaN(Number(grossKg)) || Number.isNaN(Number(tareGr))) return null;
  const tareKg = Number(tareGr) / TARE_GR_TO_KG;
  return Math.max(0, Number(grossKg) - tareKg);
}

interface Nomination {
  nominationId: number;
  userId?: number;
  nominationName: string;
  vesselName: string;
  clientName: string;
  expectWeight: number | null;
  currentWeight: number;
  containerNumber: number;
  approved?: boolean;
  createdAt: string;
  updatedAt: string;
  user?: { userId: number; firstName: string; lastName: string };
  inspectors?: Array<{ userId: number; firstName: string; lastName: string }>;
}

interface CreateNominationRequest {
  userId: number;
  inspectorIds: number[];
  nominationName: string;
  vesselName: string;
  clientName: string;
  expectWeight: number | null;
  containerNumber: number;
}

interface User {
  userId: number;
  firstName: string;
  lastName: string;
  login: string;
  roleId?: number;
}

interface ContainerItem {
  containerId: number;
  nominationId: number;
  number: string;
  agencySeal: string;
  bagsNumber: number;
  avgGrossWeightPerBag: number | null;
  tareWeightPerBag: number;
  totalGrossWeight: number | null;
  totalTareWeight: number | null;
  totalNetWeight: number | null;
  isEnded?: boolean | null;
  createdAt: string;
  updatedAt: string;
  changeReason?: string | null;
  /** Seal photo filename (without seal_photo/ prefix) for display */
  sealPhotoName?: string | null;
  nomination?: {
    nominationId: number;
    vesselName: string;
    nominationName?: string;
  };
}

const INSPECTOR_ROLE_ID = 3;
const editNominationFormDefaults: CreateNominationRequest = {
  userId: 0,
  inspectorIds: [],
  nominationName: '',
  vesselName: '',
  clientName: 'WILMAR SUGAR PTE LTD',
  expectWeight: null,
  containerNumber: 0,
};

const NominationDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.roleId === 1;

  const [nomination, setNomination] = useState<Nomination | null>(null);
  const [nominationLoading, setNominationLoading] = useState(true);
  const [nominationError, setNominationError] = useState<string | null>(null);
  const [containers, setContainers] = useState<ContainerItem[]>([]);
  const [filteredContainers, setFilteredContainers] = useState<ContainerItem[]>([]);
  const [containersLoading, setContainersLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [selectedContainer, setSelectedContainer] = useState<ContainerItem | null>(null);
  const [sealPhotoObjectUrl, setSealPhotoObjectUrl] = useState<string | null>(null);
  const [containerPhotosList, setContainerPhotosList] = useState<Array<{ base64: string; contentType: string; name: string }>>([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingContainer, setEditingContainer] = useState<ContainerItem | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const [users, setUsers] = useState<User[]>([]);
  const [clientOptions, setClientOptions] = useState<string[]>([]);
  const [editNominationDialogOpen, setEditNominationDialogOpen] = useState(false);
  const [editNominationFormData, setEditNominationFormData] = useState<CreateNominationRequest>(editNominationFormDefaults);
  const [isUpdatingNomination, setIsUpdatingNomination] = useState(false);
  const [editNominationError, setEditNominationError] = useState<string | null>(null);
  const [editNominationSuccess, setEditNominationSuccess] = useState(false);
  const [deleteNominationDialogOpen, setDeleteNominationDialogOpen] = useState(false);
  const [isDeletingNomination, setIsDeletingNomination] = useState(false);
  const [deleteNominationError, setDeleteNominationError] = useState<string | null>(null);
  const [downloadingDocument, setDownloadingDocument] = useState(false);
  const [documentError, setDocumentError] = useState<string | null>(null);
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const [editSuccess, setEditSuccess] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingContainer, setDeletingContainer] = useState<ContainerItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [changeReasonModalOpen, setChangeReasonModalOpen] = useState(false);
  const [changeReasonModalText, setChangeReasonModalText] = useState('');
  const [formData, setFormData] = useState({
    number: '',
    agencySeal: '',
    bagsNumber: 0,
    avgGrossWeightPerBag: 0 as number | null,
    tareWeightPerBag: 0,
    isEnded: false,
  });
  const [tareWeightPreset, setTareWeightPreset] = useState<80 | 150 | 'custom' | ''>('custom');
  const [avgGrossPreset, setAvgGrossPreset] = useState<25 | 50 | 1000 | 'custom'>('custom');

  const nominationId = id ? parseInt(id, 10) : NaN;
  const inspectorsList = users.filter((u) => u.roleId === INSPECTOR_ROLE_ID);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await usersAPI.getAll();
        if (res.data?.status === 'success' && Array.isArray(res.data.data)) {
          setUsers(res.data.data);
        }
      } catch {
        setUsers([]);
      }
    };
    load();
  }, []);

  const computeDerived = () => {
    const avg = formData.avgGrossWeightPerBag != null ? Number(formData.avgGrossWeightPerBag) : 0;
    const totalGrossWeight = Number(formData.bagsNumber) * avg;
    const totalTareWeightGr = Number(formData.bagsNumber) * Number(formData.tareWeightPerBag);
    const totalNetWeight = getDisplayTotalNetKg(totalGrossWeight, totalTareWeightGr) ?? 0;
    return { totalGrossWeight, totalTareWeight: totalTareWeightGr, totalNetWeight };
  };

  useEffect(() => {
    if (!id || isNaN(nominationId)) return;
    const load = async () => {
      setNominationLoading(true);
      setNominationError(null);
      try {
        const resp = await nominationsAPI.getById(nominationId);
        if (resp.data?.status === 'success' && resp.data?.data) {
          setNomination(resp.data.data);
        } else {
          setNominationError('Nomination not found');
          setNomination(null);
        }
      } catch (e: any) {
        setNominationError(e.response?.data?.message || 'Failed to load nomination');
        setNomination(null);
      } finally {
        setNominationLoading(false);
      }
    };
    load();
  }, [id, nominationId]);

  const fetchContainers = async () => {
    if (!id || isNaN(nominationId)) return;
    setContainersLoading(true);
    try {
      const resp = await containersAPI.getByNomination(nominationId);
      if (resp.data?.status === 'success' && Array.isArray(resp.data?.data)) {
        setContainers(resp.data.data);
        setFilteredContainers(resp.data.data);
      } else {
        setContainers([]);
        setFilteredContainers([]);
      }
    } catch {
      setContainers([]);
      setFilteredContainers([]);
    } finally {
      setContainersLoading(false);
    }
  };

  useEffect(() => {
    if (!id || isNaN(nominationId)) return;
    fetchContainers();
  }, [id, nominationId]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredContainers(containers);
    } else {
      const filtered = containers.filter(
        (c) =>
          c.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.agencySeal.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.nomination?.vesselName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredContainers(filtered);
    }
  }, [searchTerm, containers]);

  useEffect(() => {
    if (!photoModalOpen || !selectedContainer) return;
    let sealUrl: string | null = null;
    setPhotosLoading(true);
    const cid = selectedContainer.containerId;
    const load = async () => {
      try {
        const r = await containersAPI.getSealPhoto(cid);
        const blob = r.data as Blob;
        if (blob && blob.size > 0) {
          sealUrl = URL.createObjectURL(blob);
          setSealPhotoObjectUrl(sealUrl);
        } else {
          setSealPhotoObjectUrl(null);
        }
      } catch {
        setSealPhotoObjectUrl(null);
      }
      try {
        const ext = await containersAPI.getContainerPhotosExtracted(cid);
        if (ext.data?.status === 'success' && ext.data?.data?.images) {
          setContainerPhotosList(ext.data.data.images);
        } else {
          setContainerPhotosList([]);
        }
      } catch {
        setContainerPhotosList([]);
      }
      setPhotosLoading(false);
    };
    load();
    return () => {
      if (sealUrl) URL.revokeObjectURL(sealUrl);
    };
  }, [photoModalOpen, selectedContainer?.containerId]);

  const handleViewPhotos = (container: ContainerItem) => {
    setSelectedContainer(container);
    setPhotoModalOpen(true);
  };

  const handleClosePhotoModal = () => {
    if (sealPhotoObjectUrl) URL.revokeObjectURL(sealPhotoObjectUrl);
    setPhotoModalOpen(false);
    setSealPhotoObjectUrl(null);
    setContainerPhotosList([]);
    setSelectedContainer(null);
  };

  const handleEditContainer = (containerId: number) => {
    const container = containers.find((c) => c.containerId === containerId);
    if (!container) return;
    setEditingContainer(container);
    const tare = Number(container.tareWeightPerBag) || 0;
    const tarePreset: 80 | 150 | 'custom' | '' = tare === 80 ? 80 : tare === 150 ? 150 : tare === 0 ? '' : 'custom';
    setTareWeightPreset(tarePreset);
    const avg = container.avgGrossWeightPerBag != null ? Number(container.avgGrossWeightPerBag) : null;
    const avgPreset: 25 | 50 | 1000 | 'custom' = avg === 25 ? 25 : avg === 50 ? 50 : avg === 1000 ? 1000 : 'custom';
    setAvgGrossPreset(avgPreset);
    setFormData({
      number: container.number,
      agencySeal: container.agencySeal,
      bagsNumber: container.bagsNumber,
      avgGrossWeightPerBag: container.avgGrossWeightPerBag ?? 0,
      tareWeightPerBag: tare,
      isEnded: container.isEnded ?? false,
    });
    setEditError(null);
    setEditSuccess(false);
    setEditDialogOpen(true);
  };

  const handleFormChange = (field: keyof typeof formData, value: string | number | boolean | null) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingContainer(null);
    setEditError(null);
    setEditSuccess(false);
  };

  const handleUpdateContainer = async () => {
    if (!editingContainer) return;
    try {
      setIsUpdating(true);
      setEditError(null);
      if (!formData.number.trim() || !formData.agencySeal.trim()) {
        setEditError('Number and Agency Seal are required');
        return;
      }
      if (formData.bagsNumber <= 0) {
        setEditError('Bags number must be positive');
        return;
      }
      const payload = {
        number: formData.number,
        agencySeal: formData.agencySeal,
        bagsNumber: formData.bagsNumber,
        avgGrossWeightPerBag: formData.avgGrossWeightPerBag ?? undefined,
        tareWeightPerBag: formData.tareWeightPerBag,
        isEnded: formData.isEnded,
      };
      const resp = await containersAPI.update(editingContainer.containerId, payload);
      if (resp.data.status === 'success') {
        setEditSuccess(true);
        await fetchContainers();
        setTimeout(() => handleCloseEditDialog(), 800);
      }
    } catch (e: any) {
      setEditError(e.response?.data?.message || 'Failed to update container');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteContainer = (containerId: number) => {
    const container = containers.find((c) => c.containerId === containerId);
    if (!container) return;
    setDeletingContainer(container);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeletingContainer(null);
    setDeleteError(null);
  };

  const handleOpenChangeReason = (text: string) => {
    setChangeReasonModalText(text);
    setChangeReasonModalOpen(true);
  };

  const handleCloseChangeReasonModal = () => {
    setChangeReasonModalOpen(false);
    setChangeReasonModalText('');
  };

  const handleConfirmDelete = async () => {
    if (!deletingContainer) return;
    try {
      setIsDeleting(true);
      setDeleteError(null);
      await containersAPI.remove(deletingContainer.containerId);
      await fetchContainers();
      handleCloseDeleteDialog();
    } catch (e: any) {
      setDeleteError(e.response?.data?.message || 'Failed to delete container');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();
  const formatDateTime = (dateString: string) => new Date(dateString).toLocaleString();
  /** Same format as Containers: toLocaleString() split into date and time for two-line display */
  const formatDateTimeParts = (dateString: string): { date: string; time: string } => {
    const full = new Date(dateString).toLocaleString();
    const sep = full.includes(', ') ? ', ' : ' ';
    const parts = full.split(sep);
    if (parts.length >= 2) return { date: parts[0], time: parts.slice(1).join(sep) };
    return { date: full, time: '' };
  };

  const loadNomination = async () => {
    if (!id || isNaN(nominationId)) return;
    try {
      const resp = await nominationsAPI.getById(nominationId);
      if (resp.data?.status === 'success' && resp.data?.data) {
        setNomination(resp.data.data);
      }
    } catch {
      // keep current nomination on error
    }
  };

  const handleEditNomination = () => {
    if (!nomination) return;
    nominationsAPI.getAll().then((response) => {
      const names: string[] = (response.data?.data ?? [])
        .map((item: Nomination) => item.clientName?.trim())
        .filter((name: string | undefined): name is string => !!name);
      setClientOptions(Array.from(new Set(['WILMAR SUGAR PTE LTD', nomination.clientName, ...names].filter(Boolean)))
        .sort((first, second) => first.localeCompare(second)));
    }).catch(() => setClientOptions(['WILMAR SUGAR PTE LTD', nomination.clientName].filter(Boolean)));
    setEditNominationFormData({
      userId: nomination.userId ?? 0,
      inspectorIds: nomination.inspectors?.map((i) => i.userId) ?? [],
      nominationName: nomination.nominationName,
      vesselName: nomination.vesselName,
      clientName: nomination.clientName ?? 'WILMAR SUGAR PTE LTD',
      expectWeight: nomination.expectWeight,
      containerNumber: nomination.containerNumber,
    });
    setEditNominationDialogOpen(true);
    setEditNominationError(null);
    setEditNominationSuccess(false);
  };

  const handleEditNominationFormChange = (field: keyof CreateNominationRequest, value: unknown) => {
    setEditNominationFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpdateNomination = async () => {
    if (!nomination) return;
    try {
      setIsUpdatingNomination(true);
      setEditNominationError(null);
      if (!editNominationFormData.userId || !editNominationFormData.vesselName || editNominationFormData.containerNumber <= 0) {
        setEditNominationError('Please fill in all required fields with valid values');
        return;
      }
      if (!editNominationFormData.clientName.trim() || editNominationFormData.clientName.trim().length > 255) {
        setEditNominationError('Enter a client name (up to 255 characters)');
        return;
      }
      if (editNominationFormData.expectWeight != null && editNominationFormData.expectWeight < 0) {
        setEditNominationError('Expected weight cannot be negative');
        return;
      }
      await nominationsAPI.update(nomination.nominationId, editNominationFormData);
      setEditNominationSuccess(true);
      await loadNomination();
      setTimeout(() => {
        setEditNominationDialogOpen(false);
        setEditNominationSuccess(false);
      }, 1500);
    } catch (error: any) {
      setEditNominationError(error.response?.data?.message || 'Failed to update nomination');
    } finally {
      setIsUpdatingNomination(false);
    }
  };

  const handleCloseEditNominationDialog = () => {
    setEditNominationDialogOpen(false);
    setEditNominationError(null);
    setEditNominationSuccess(false);
    setEditNominationFormData(editNominationFormDefaults);
  };

  const handleDeleteNomination = () => {
    setDeleteNominationError(null);
    setDeleteNominationDialogOpen(true);
  };

  const handleConfirmDeleteNomination = async () => {
    if (!nomination) return;
    try {
      setIsDeletingNomination(true);
      setDeleteNominationError(null);
      await nominationsAPI.remove(nomination.nominationId);
      navigate('/nominations');
    } catch (error: any) {
      setDeleteNominationError(error.response?.data?.message || 'Failed to delete nomination');
    } finally {
      setIsDeletingNomination(false);
    }
  };

  const handleCloseDeleteNominationDialog = () => {
    setDeleteNominationDialogOpen(false);
    setDeleteNominationError(null);
  };

  const handleDownloadDocument = async () => {
    if (!nomination) return;
    try {
      setDownloadingDocument(true);
      setDocumentError(null);
      const response = await documentsAPI.downloadNominationReport(nomination.nominationId);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `nomination-${nomination.nominationId}-report.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      setDocumentError(error.response?.data?.message || 'Failed to download document');
    } finally {
      setDownloadingDocument(false);
    }
  };

  const handleOpenApproveConfirm = () => setApproveConfirmOpen(true);
  const handleCloseApproveConfirm = () => setApproveConfirmOpen(false);

  const handleApproveNomination = async () => {
    if (!nomination || !isAdmin) return;
    try {
      setApproveLoading(true);
      await nominationsAPI.approve([nomination.nominationId]);
      setApproveConfirmOpen(false);
      await loadNomination();
    } catch {
      // could set error state
    } finally {
      setApproveLoading(false);
    }
  };

  if (nominationLoading && !nomination) {
    return (
      <Layout>
        <Container maxWidth={false} sx={{ mt: 2, mb: 4 }}>
          <Typography>Loading...</Typography>
        </Container>
      </Layout>
    );
  }

  if (nominationError || !nomination) {
    return (
      <Layout>
        <Container maxWidth={false} sx={{ mt: 2, mb: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {nominationError || 'Nomination not found'}
          </Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/nominations')}
            sx={{
              borderColor: '#CA4300',
              color: '#CA4300',
              borderRadius: 1,
              boxShadow: 2,
              '&:hover': {
                borderColor: '#A03600',
                backgroundColor: 'rgba(202, 67, 0, 0.08)',
                boxShadow: 2,
              },
            }}
          >
            Back to Nominations
          </Button>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth={false} sx={{ mt: 2, mb: 4 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/nominations')}
          sx={{
            mb: 2,
            borderColor: '#CA4300',
            color: '#CA4300',
            borderRadius: 1,
            boxShadow: 2,
            '&:hover': {
              borderColor: '#A03600',
              backgroundColor: 'rgba(202, 67, 0, 0.08)',
              boxShadow: 2,
            },
          }}
        >
          Back to Nominations
        </Button>

        {/* Title */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h4" component="h1">
            Nomination Management — {nomination.nominationName}
          </Typography>
        </Paper>

        {/* Details */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              Nomination details
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {documentError && (
                <Typography variant="caption" color="error" sx={{ mr: 1 }}>
                  {documentError}
                </Typography>
              )}
              <Tooltip title={nomination.approved ? 'Download Report' : 'Report available after approval'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={handleDownloadDocument}
                    disabled={!nomination.approved || downloadingDocument}
                    sx={{ color: nomination.approved ? '#1976d2' : undefined }}
                  >
                    <DescriptionIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton size="small" onClick={handleEditNomination}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" onClick={handleDeleteNomination} sx={{ color: '#b91c1c' }}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
              {isAdmin && (
                <Tooltip title={nomination.approved ? 'Already approved' : 'Approve nomination'}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleOpenApproveConfirm}
                      disabled={nomination.approved || approveLoading}
                      sx={{
                        color: nomination.approved ? undefined : '#CA4300',
                        '&:hover': { backgroundColor: nomination.approved ? undefined : 'rgba(202, 67, 0, 0.08)' },
                      }}
                    >
                      <ApprovementIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                Vessel Name
              </Typography>
              <Typography variant="body1">{nomination.vesselName}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Client (Principal)
              </Typography>
              <Typography variant="body1">{nomination.clientName}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Expected Weight
              </Typography>
              <Typography variant="body1">{nomination.expectWeight != null ? Number(nomination.expectWeight).toFixed(2) : '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Current Weight
              </Typography>
              <Typography variant="body1">{Number(nomination.currentWeight).toFixed(2)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Number of containers
              </Typography>
              <Typography variant="body1">{nomination.containerNumber}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Created At
              </Typography>
              <Typography variant="body1">{formatDateTime(nomination.createdAt)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Updated At
              </Typography>
              <Typography variant="body1">{formatDateTime(nomination.updatedAt)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Approved
              </Typography>
              <Typography variant="body1">{nomination.approved ? 'Yes' : 'No'}</Typography>
            </Box>
          </Box>
        </Paper>

        {/* Search */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              label="Search Containers"
              placeholder="Search by container number, agency seal, vessel name, or ports..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              Showing {filteredContainers.length} of {containers.length} containers
            </Typography>
          </Box>
        </Paper>

        {/* Containers Table */}
        <Paper>
          <TableContainer
            sx={{
              overflowX: 'auto',
              maxWidth: '100%',
              '&::-webkit-scrollbar': { height: '8px' },
              '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: '4px' },
              '&::-webkit-scrollbar-thumb': { backgroundColor: '#CA4300', borderRadius: '4px' },
              '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#A03600' },
            }}
          >
            <Table
              size="small"
              sx={{
                tableLayout: 'fixed',
                minWidth: '1210px',
                width: '100%',
                '& .MuiTableHead-root': {
                  '& .MuiTableRow-root': {
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                  },
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '120px', minWidth: '120px' }}>Container Number</TableCell>
                  <TableCell sx={{ width: '100px', minWidth: '100px' }}>Agency Seal</TableCell>
                  <TableCell sx={{ width: '80px', minWidth: '80px' }}>Bags</TableCell>
                  <TableCell sx={{ width: '90px', minWidth: '90px' }}>Is ended</TableCell>
                  <TableCell sx={{ width: '120px', minWidth: '120px' }}>Avg Gross/Bag</TableCell>
                  <TableCell sx={{ width: '100px', minWidth: '100px' }}>Tare/Bag</TableCell>
                  <TableCell sx={{ width: '120px', minWidth: '120px' }}>Total Gross</TableCell>
                  <TableCell sx={{ width: '120px', minWidth: '120px' }}>Total Tare</TableCell>
                  <TableCell sx={{ width: '120px', minWidth: '120px' }}>Total Net</TableCell>
                  <TableCell sx={{ width: '120px', minWidth: '120px' }}>Updated At</TableCell>
                  <TableCell sx={{ width: '120px', minWidth: '120px' }} align="left">Changes</TableCell>
                  <TableCell sx={{ width: '100px', minWidth: '100px' }} align="left">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {containersLoading ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center">
                      Loading containers...
                    </TableCell>
                  </TableRow>
                ) : filteredContainers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center">
                      No containers found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredContainers.map((container) => (
                    <TableRow key={container.containerId} hover>
                      <TableCell>
                        <Chip
                          label={container.number}
                          sx={{ backgroundColor: '#CA4300', color: '#ffffff', fontWeight: 'bold' }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography
                          variant="body2"
                          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '90px' }}
                        >
                          {container.agencySeal}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{container.bagsNumber.toLocaleString()}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={container.isEnded ? 'Yes' : 'No'}
                          size="small"
                          color={container.isEnded ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {container.avgGrossWeightPerBag != null ? `${Number(container.avgGrossWeightPerBag).toFixed(2)} kg` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {container.tareWeightPerBag != null && !Number.isNaN(Number(container.tareWeightPerBag))
                            ? Number(container.tareWeightPerBag).toFixed(2)
                            : '—'}{' '}
                          gr
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {container.totalGrossWeight != null && !Number.isNaN(Number(container.totalGrossWeight))
                            ? Number(container.totalGrossWeight).toLocaleString()
                            : '—'}{' '}
                          kg
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {container.totalTareWeight != null && !Number.isNaN(Number(container.totalTareWeight))
                            ? Number(container.totalTareWeight).toLocaleString()
                            : '—'}{' '}
                          gr
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {(() => { const n = getDisplayTotalNetKg(container.totalGrossWeight, container.totalTareWeight); return n != null ? `${n.toLocaleString()} kg` : '—'; })()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const { date, time } = formatDateTimeParts(container.updatedAt);
                          return (
                            <>
                              <Typography variant="body2" component="span" display="block">
                                {date}
                              </Typography>
                              {time ? (
                                <Typography variant="body2" component="span" display="block">
                                  {time}
                                </Typography>
                              ) : null}
                            </>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {container.changeReason ? (
                          <IconButton
                            size="small"
                            onClick={() => handleOpenChangeReason(container.changeReason!)}
                            sx={{ color: '#1976d2' }}
                          >
                            <InfoIcon />
                          </IconButton>
                        ) : (
                          <Tooltip title="No information">
                            <span>
                              <IconButton size="small" disabled sx={{ color: undefined }}>
                                <InfoIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Tooltip title="View Photos">
                            <IconButton size="small" onClick={() => handleViewPhotos(container)} sx={{ color: '#56c785' }}>
                              <CameraIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEditContainer(container.containerId)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDeleteContainer(container.containerId)} sx={{ color: '#b91c1c' }}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Edit Container Dialog */}
        <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
          <DialogTitle>Edit Container</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {editError && <Alert severity="error">{editError}</Alert>}
              {editSuccess && <Alert severity="success">Container updated successfully</Alert>}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Container Number"
                  value={formData.number}
                  onChange={(e) => handleFormChange('number', e.target.value)}
                  required
                />
                <TextField
                  fullWidth
                  label="Agency Seal"
                  value={formData.agencySeal}
                  onChange={(e) => handleFormChange('agencySeal', e.target.value)}
                  required
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  checked={formData.isEnded}
                  onChange={(e) => handleFormChange('isEnded', e.target.checked)}
                  color="primary"
                />
                <Typography variant="body2">Is ended</Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Bags Number"
                  value={formData.bagsNumber}
                  onChange={(e) => handleFormChange('bagsNumber', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 1 }}
                  required
                />
                <FormControl fullWidth>
                  <InputLabel>Avg Gross/Bag (kg)</InputLabel>
                  <Select
                    value={typeof avgGrossPreset === 'number' ? String(avgGrossPreset) : formData.avgGrossWeightPerBag == null ? (avgGrossPreset === 'custom' ? 'custom' : '') : 'custom'}
                    label="Avg Gross/Bag (kg)"
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '25' || v === '50' || v === '1000') {
                        const n = Number(v) as 25 | 50 | 1000;
                        setAvgGrossPreset(n);
                        handleFormChange('avgGrossWeightPerBag', n);
                      } else if (v === '') {
                        setAvgGrossPreset('custom');
                        handleFormChange('avgGrossWeightPerBag', null);
                      } else {
                        setAvgGrossPreset('custom');
                      }
                    }}
                  >
                    <MenuItem value="">—</MenuItem>
                    {AVG_GROSS_OPTIONS.map((v) => (
                      <MenuItem key={v} value={String(v)}>{v}</MenuItem>
                    ))}
                    <MenuItem value="custom">Custom (enter below)</MenuItem>
                  </Select>
                </FormControl>
                {(avgGrossPreset === 'custom' || (formData.avgGrossWeightPerBag != null && !AVG_GROSS_OPTIONS.includes(formData.avgGrossWeightPerBag as 25 | 50 | 1000))) && (
                  <TextField
                    fullWidth
                    type="number"
                    label="Avg Gross/Bag (kg) — manual"
                    value={formData.avgGrossWeightPerBag ?? ''}
                    onChange={(e) => handleFormChange('avgGrossWeightPerBag', parseFloat(e.target.value) || null)}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                )}
              </Box>
              <FormControl fullWidth required>
                <InputLabel>Tare Weight/Bag (gr)</InputLabel>
                <Select
                  value={tareWeightPreset === 'custom' ? 'custom' : tareWeightPreset === '' ? '' : String(tareWeightPreset)}
                  label="Tare Weight/Bag (gr)"
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '80' || v === '150') {
                      const n = (v === '80' ? 80 : 150) as 80 | 150;
                      setTareWeightPreset(n);
                      handleFormChange('tareWeightPerBag', n);
                    } else if (v === '') {
                      setTareWeightPreset('');
                      handleFormChange('tareWeightPerBag', 0);
                    } else {
                      setTareWeightPreset('custom');
                    }
                  }}
                >
                  <MenuItem value="">—</MenuItem>
                  {TARE_WEIGHT_PRESETS.map((v) => (
                    <MenuItem key={v} value={String(v)}>{v}</MenuItem>
                  ))}
                  <MenuItem value="custom">Custom value (enter below)</MenuItem>
                </Select>
              </FormControl>
              {tareWeightPreset === 'custom' && (
                <TextField
                  fullWidth
                  type="number"
                  label="Tare Weight/Bag (gr) — manual"
                  value={formData.tareWeightPerBag}
                  onChange={(e) => handleFormChange('tareWeightPerBag', parseInt(e.target.value, 10) || 0)}
                  inputProps={{ min: 0, step: 1 }}
                />
              )}
              {(() => {
                const d = computeDerived();
                return (
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                    <TextField fullWidth label="Total Gross (kg)" value={d.totalGrossWeight.toLocaleString()} InputProps={{ readOnly: true }} />
                    <TextField fullWidth label="Total Tare (gr)" value={d.totalTareWeight.toLocaleString()} InputProps={{ readOnly: true }} />
                    <TextField fullWidth label="Total Net (kg)" value={d.totalNetWeight != null && !Number.isNaN(Number(d.totalNetWeight)) ? Number(d.totalNetWeight).toLocaleString() : '—'} InputProps={{ readOnly: true }} />
                  </Box>
                );
              })()}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={handleCloseEditDialog}
              disabled={isUpdating}
              sx={{ borderColor: '#CA4300', color: '#CA4300', borderRadius: 1, boxShadow: 2, '&:hover': { borderColor: '#A03600', backgroundColor: 'rgba(202, 67, 0, 0.08)', boxShadow: 2 } }}
            >
              Cancel
            </Button>
            <Button onClick={handleUpdateContainer} variant="contained" disabled={isUpdating} sx={{ backgroundColor: '#CA4300', '&:hover': { backgroundColor: '#A03600' } }}>
              {isUpdating ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Container Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Delete Container</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {deleteError && <Alert severity="error">{deleteError}</Alert>}
              <Typography variant="body1">
                Are you sure you want to delete the container &quot;{deletingContainer?.number}&quot;?
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={handleCloseDeleteDialog}
              disabled={isDeleting}
              sx={{ borderColor: '#CA4300', color: '#CA4300', borderRadius: 1, boxShadow: 2, '&:hover': { borderColor: '#A03600', backgroundColor: 'rgba(202, 67, 0, 0.08)', boxShadow: 2 } }}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmDelete} variant="contained" disabled={isDeleting} sx={{ backgroundColor: '#b91c1c', '&:hover': { backgroundColor: '#991b1b' } }}>
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Change Reason Modal */}
        <Dialog open={changeReasonModalOpen} onClose={handleCloseChangeReasonModal} maxWidth="sm" fullWidth>
          <DialogTitle>Change Reason</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {changeReasonModalText || '—'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={handleCloseChangeReasonModal}
              sx={{
                borderColor: '#CA4300',
                color: '#CA4300',
                borderRadius: 1,
                boxShadow: 2,
                '&:hover': { borderColor: '#A03600', backgroundColor: 'rgba(202, 67, 0, 0.08)', boxShadow: 2 },
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Photo Modal */}
        <Dialog open={photoModalOpen} onClose={handleClosePhotoModal} maxWidth="md" fullWidth>
          <DialogTitle>Container Photos - {selectedContainer?.number}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              {photosLoading ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  Loading photos...
                </Typography>
              ) : (
                <>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Seal Photo
                    </Typography>
                    {sealPhotoObjectUrl ? (
                      <Box sx={{ textAlign: 'center' }}>
                        <img
                          src={sealPhotoObjectUrl}
                          alt="Seal"
                          style={{ maxWidth: '100%', maxHeight: '400px', objectFit: 'contain', borderRadius: 8, border: '1px solid #ddd' }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                          {selectedContainer?.sealPhotoName ?? '—'}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        No seal photo available
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Container Photo
                    </Typography>
                    {containerPhotosList.length > 0 ? (
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
                        {containerPhotosList.map((img, idx) => (
                          <Box key={idx} sx={{ textAlign: 'center' }}>
                            <img
                              src={`data:${img.contentType};base64,${img.base64}`}
                              alt={img.name}
                              style={{ width: '100%', maxHeight: '300px', objectFit: 'contain', borderRadius: 8, border: '1px solid #ddd' }}
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                              {img.name}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        No container photo available
                      </Typography>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={handleClosePhotoModal}
              sx={{
                borderColor: '#CA4300',
                color: '#CA4300',
                borderRadius: 1,
                boxShadow: 2,
                '&:hover': {
                  borderColor: '#A03600',
                  backgroundColor: 'rgba(202, 67, 0, 0.08)',
                  boxShadow: 2,
                },
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Nomination Dialog */}
        <Dialog open={editNominationDialogOpen} onClose={handleCloseEditNominationDialog} maxWidth="md" fullWidth>
          <DialogTitle>Edit Nomination</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              {editNominationError && <Alert severity="error">{editNominationError}</Alert>}
              {editNominationSuccess && <Alert severity="success">Nomination updated successfully!</Alert>}
              <FormControl fullWidth required>
                <InputLabel>User</InputLabel>
                <Select
                  value={editNominationFormData.userId}
                  onChange={(e) => handleEditNominationFormChange('userId', Number(e.target.value))}
                  label="User"
                >
                  {users.map((user) => (
                    <MenuItem key={user.userId} value={user.userId}>
                      {user.firstName} {user.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={inspectorsList}
                value={inspectorsList.filter((user) => editNominationFormData.inspectorIds.includes(user.userId))}
                onChange={(_, value) => handleEditNominationFormChange('inspectorIds', value.map((user) => user.userId))}
                isOptionEqualToValue={(option, value) => option.userId === value.userId}
                getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                noOptionsText="No inspectors found"
                ListboxProps={{
                  sx: {
                    maxHeight: 280,
                    overflowY: 'auto',
                  },
                }}
                renderOption={(props, option, { selected }) => {
                  const { key, ...optionProps } = props;
                  return (
                    <li key={option.userId} {...optionProps}>
                      <Checkbox checked={selected} sx={{ mr: 1 }} />
                      {option.firstName} {option.lastName}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Inspectors"
                    placeholder="Search and select inspectors"
                    helperText={`Selected: ${editNominationFormData.inspectorIds.length}`}
                  />
                )}
              />
              <TextField
                fullWidth
                required
                label="Nomination Name"
                value={editNominationFormData.nominationName}
                onChange={(e) => handleEditNominationFormChange('nominationName', e.target.value)}
              />
              <TextField
                fullWidth
                required
                label="Vessel Name"
                value={editNominationFormData.vesselName}
                onChange={(e) => handleEditNominationFormChange('vesselName', e.target.value)}
              />
              <Autocomplete
                freeSolo
                options={clientOptions}
                value={editNominationFormData.clientName}
                inputValue={editNominationFormData.clientName}
                onChange={(_, value) => handleEditNominationFormChange('clientName', value ?? '')}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input' || reason === 'clear') handleEditNominationFormChange('clientName', value);
                }}
                renderInput={(params) => (
                  <TextField {...params} fullWidth required label="Client (Principal)" inputProps={{ ...params.inputProps, maxLength: 255 }} />
                )}
              />
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Expected Weight (tons)"
                  value={editNominationFormData.expectWeight ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    handleEditNominationFormChange('expectWeight', v === '' ? null : parseFloat(v) || 0);
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                />
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Container Number"
                  value={editNominationFormData.containerNumber}
                  onChange={(e) => handleEditNominationFormChange('containerNumber', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 1 }}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={handleCloseEditNominationDialog}
              disabled={isUpdatingNomination}
              sx={{
                borderColor: '#CA4300',
                color: '#CA4300',
                borderRadius: 1,
                boxShadow: 2,
                '&:hover': { borderColor: '#A03600', backgroundColor: 'rgba(202, 67, 0, 0.08)', boxShadow: 2 },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateNomination}
              variant="contained"
              disabled={isUpdatingNomination}
              sx={{ backgroundColor: '#CA4300', '&:hover': { backgroundColor: '#A03600' } }}
            >
              {isUpdatingNomination ? 'Updating...' : 'Update Nomination'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Nomination Confirmation Dialog */}
        <Dialog open={deleteNominationDialogOpen} onClose={handleCloseDeleteNominationDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Delete Nomination</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              {deleteNominationError && <Alert severity="error">{deleteNominationError}</Alert>}
              <Typography variant="body1">
                Are you sure you want to delete the nomination "{nomination?.vesselName}"?
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={handleCloseDeleteNominationDialog}
              disabled={isDeletingNomination}
              sx={{
                borderColor: '#CA4300',
                color: '#CA4300',
                borderRadius: 1,
                boxShadow: 2,
                '&:hover': { borderColor: '#A03600', backgroundColor: 'rgba(202, 67, 0, 0.08)', boxShadow: 2 },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDeleteNomination}
              variant="contained"
              disabled={isDeletingNomination}
              sx={{ backgroundColor: '#b91c1c', '&:hover': { backgroundColor: '#991b1b' } }}
            >
              {isDeletingNomination ? 'Deleting...' : 'Delete Nomination'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Approve Nomination Confirmation Dialog */}
        <Dialog open={approveConfirmOpen} onClose={handleCloseApproveConfirm} maxWidth="sm" fullWidth>
          <DialogTitle>Approve Nomination</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mt: 1 }}>
              Are you sure you want to approve nomination "{nomination?.nominationName}"?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={handleCloseApproveConfirm}
              disabled={approveLoading}
              sx={{
                borderColor: '#CA4300',
                color: '#CA4300',
                borderRadius: 1,
                boxShadow: 2,
                '&:hover': { borderColor: '#A03600', backgroundColor: 'rgba(202, 67, 0, 0.08)', boxShadow: 2 },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApproveNomination}
              variant="contained"
              disabled={approveLoading}
              sx={{ backgroundColor: '#CA4300', '&:hover': { backgroundColor: '#A03600' } }}
            >
              {approveLoading ? 'Approving...' : 'Approve'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export { NominationDetails };
export default NominationDetails;
